import { create } from 'zustand';
import api from '../lib/api';
import type { GmailConnectionStatus, GmailConnectionItem, SyncStatus } from '../types';

interface GmailState {
  // Current user's connection status
  connected: boolean;
  googleEmail: string | null;
  syncStatus: SyncStatus | null;
  syncError: string | null;
  lastSyncAt: string | null;
  connectedAt: string | null;
  calendarSyncEnabled: boolean;
  needsCalendarReconnect: boolean;
  canSend: boolean;

  // Admin: all connections
  allConnections: GmailConnectionItem[];

  // UI state
  isLoading: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  error: string | null;

  // Actions
  fetchStatus: () => Promise<void>;
  initiateConnect: () => Promise<void>;
  initiateCalendarSync: () => Promise<void>;
  disconnect: () => Promise<void>;
  fetchAllConnections: () => Promise<void>;
  disconnectUser: (connectionId: string) => Promise<void>;
  clearError: () => void;
}

export const useGmailStore = create<GmailState>((set, get) => ({
  // Initial state
  connected: false,
  googleEmail: null,
  syncStatus: null,
  syncError: null,
  lastSyncAt: null,
  connectedAt: null,
  calendarSyncEnabled: false,
  needsCalendarReconnect: false,
  canSend: false,

  allConnections: [],

  isLoading: false,
  isConnecting: false,
  isDisconnecting: false,
  error: null,

  /**
   * Fetch current user's Gmail connection status
   */
  fetchStatus: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.get('/api/gmail/status');
      const data: GmailConnectionStatus = response.data;

      if (data.connected && data.connection) {
        set({
          connected: true,
          googleEmail: data.connection.googleEmail,
          syncStatus: data.connection.syncStatus,
          syncError: data.connection.syncError,
          lastSyncAt: data.connection.lastSyncAt,
          connectedAt: data.connection.connectedAt,
          calendarSyncEnabled: data.connection.calendarSyncEnabled,
          needsCalendarReconnect: data.connection.needsCalendarReconnect,
          canSend: data.connection?.canSend ?? false,
          isLoading: false,
        });
      } else {
        set({
          connected: false,
          googleEmail: null,
          syncStatus: null,
          syncError: null,
          lastSyncAt: null,
          connectedAt: null,
          calendarSyncEnabled: false,
          needsCalendarReconnect: false,
          canSend: false,
          isLoading: false,
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch Gmail status:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load Gmail status',
      });
    }
  },

  /**
   * Initiate Gmail OAuth flow — redirects browser to Google consent screen
   */
  initiateConnect: async () => {
    try {
      set({ isConnecting: true, error: null });

      const response = await api.get('/api/gmail/auth-url');
      const { authUrl } = response.data;

      // Redirect to Google OAuth consent screen
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Failed to initiate Gmail connect:', error);
      set({
        isConnecting: false,
        error: error.response?.data?.message || 'Failed to start Gmail connection',
      });
    }
  },

  /**
   * Initiate Google Calendar OAuth re-auth flow — adds calendar scope to existing Gmail connection
   */
  initiateCalendarSync: async () => {
    try {
      set({ isConnecting: true, error: null });

      const response = await api.get('/api/gmail/calendar-auth-url');
      const { authUrl } = response.data;

      // Redirect to Google OAuth consent screen with calendar scope
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Failed to initiate calendar sync:', error);
      set({
        isConnecting: false,
        error: error.response?.data?.message || 'Failed to start calendar sync',
      });
    }
  },

  /**
   * Disconnect current user's Gmail
   */
  disconnect: async () => {
    try {
      set({ isDisconnecting: true, error: null });

      await api.delete('/api/gmail/connection');

      set({
        connected: false,
        googleEmail: null,
        syncStatus: null,
        syncError: null,
        lastSyncAt: null,
        connectedAt: null,
        needsCalendarReconnect: false,
        isDisconnecting: false,
      });
    } catch (error: any) {
      console.error('Failed to disconnect Gmail:', error);
      set({
        isDisconnecting: false,
        error: error.response?.data?.message || 'Failed to disconnect Gmail',
      });
    }
  },

  /**
   * Fetch all Gmail connections (admin only)
   */
  fetchAllConnections: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.get('/api/gmail/connections');
      const { connections } = response.data;

      set({
        allConnections: connections,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch Gmail connections:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load Gmail connections',
      });
    }
  },

  /**
   * Disconnect a specific user's Gmail (admin only)
   */
  disconnectUser: async (connectionId: string) => {
    try {
      set({ isDisconnecting: true, error: null });

      await api.delete(`/api/gmail/connections/${connectionId}`);

      // Remove from list or update status
      set((state) => ({
        allConnections: state.allConnections.map((conn) =>
          conn.id === connectionId
            ? { ...conn, syncStatus: 'DISCONNECTED' as SyncStatus, isActive: false }
            : conn
        ),
        isDisconnecting: false,
      }));
    } catch (error: any) {
      console.error('Failed to disconnect user Gmail:', error);
      set({
        isDisconnecting: false,
        error: error.response?.data?.message || 'Failed to disconnect Gmail connection',
      });
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },
}));
